import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import StockRequirementsGanttChartContent from "./StockRequirementsGanttChartContent";
import { GridLines, GridGroups, CurrentShiftDashedLine } from "./GanttChartGrid";
import { PropertiesLogic } from "../reducers/BusinessLogic";
import { UPDATE_PROPERTIES_ACTION_NAME } from "../reducers/properties";

import * as t2 from "../../../../lib/backend/reports.generated.types";
import type {
    UpdatePropertiesActionType,
    GanttChartSourceTypes,
    UpdatePlanningTableWidthAction,
} from "../reducers/properties";
import type { ReportState } from "../reducers/report";
import type { ReduxState } from "../reducers/reducers";
import type { ResetGanttAction } from "../reducers/common";

const getReportCreatedAt = (report: ReportState) => {
    if (!report || !report.report_data) return null;
    return report.report_data.created_at;
};

type ActionTypes = UpdatePropertiesActionType | ResetGanttAction | UpdatePlanningTableWidthAction;

type OwnProps = {
    line_group_uuid: string;
    source_type: GanttChartSourceTypes;
};

type Props = OwnProps & ReduxProps;

type State = {
    properties_loaded: boolean;
};

class StockRequirementsGanttChart extends React.Component<Props, State> {
    state: State = {
        properties_loaded: false,
    };

    phantomNode = React.createRef<SVGTextElement>();

    resizeListener: (() => null) | null = null;

    componentDidMount = () => {
        // calculate width and height
        if (!this.resizeListener) {
            this.resizeListener = this.updateDimensions();
        }

        window.addEventListener("resize", this.resizeListener);

        const properties = {
            source_type: this.props.source_type,
            line_group_uuid: this.props.line_group_uuid,
        };

        this.props.reduxDispatch({
            type: UPDATE_PROPERTIES_ACTION_NAME,
            data: properties,
        });
    };

    componentWillUnmount() {
        this.removeListeners();
    }

    removeListeners = () => {
        if (this.resizeListener) {
            window.removeEventListener("resize", this.resizeListener);
        }
    };

    updateDimensions = () => () => {
        return null;
    };

    getIsPropertiesLoaded = () => {
        return this.props.properties.loaded;
    };

    getSvgAttributes() {
        let svg_attributes = {};
        const width = this.props.properties.width;

        const height = PropertiesLogic.getHeight(this.props.properties, this.props.source_type);

        if (width || height) {
            svg_attributes = {
                width: width,
                height: height,
            };
        }

        return svg_attributes;
    }

    getOpacity = () => {
        return this.props.show_stock_requirements ? 1 : 0;
    };

    render() {
        if (!this.props.show_stock_requirements) {
            return null;
        }

        const svg_attributes = this.getSvgAttributes();
        const class_name = `${this.props.properties.no_overflow ? "" : "chart-wrapper"}`;
        const shifts = this.props.shifts ? this.props.shifts[0].shifts : [];
        return (
            <div
                id={this.props.source_type}
                style={{ zIndex: 1, opacity: this.getOpacity(), userSelect: "none" }}
                className={class_name}
            >
                <svg id="gantt-chart-stock-requirements" {...svg_attributes}>
                    <g id="grid">
                        <rect
                            height="100%"
                            width={this.props.properties.max_line_label_length ?? undefined}
                            fill="white"
                            display-outside="block"
                        />
                        <text
                            ref={this.phantomNode}
                            fontSize={"14px"}
                            fontFamily={"'Roboto', sans-serif"}
                            fontWeight={"400"}
                        />
                        <GridLines
                            gantt_chart_properties={this.props.properties}
                            source_type={this.props.source_type}
                            shifts={shifts}
                            shift_x_positions={this.props.shift_x_positions}
                        />
                        <GridGroups
                            gantt_chart_properties={this.props.properties}
                            source_type={this.props.source_type}
                            shifts={shifts}
                            shift_x_positions={this.props.shift_x_positions}
                        />
                        <CurrentShiftDashedLine
                            gantt_chart_properties={this.props.properties}
                            source_type={this.props.source_type}
                            shifts={shifts}
                            shift_x_positions={this.props.shift_x_positions}
                        />
                        <StockRequirementsGanttChartContent />
                    </g>
                </svg>
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const loading: boolean = state.gantt_chart_lines_orders.loading;
    const properties = state.gantt_chart_properties;
    const planning_table = state.gantt_chart_planning_table;
    const report = state.gantt_chart_report.report_data;
    const shift_x_positions = properties.shift_x_positions;
    let shifts: t2.ISimulationReportOrderLineShiftFeature[] = [];
    if (report) {
        shifts = report.result.line_shift_features;
    }
    return {
        report_loading: state.gantt_chart_report.report_loading,
        lines_orders_loading: loading,
        report: state.gantt_chart_report,
        created_at: getReportCreatedAt(state.gantt_chart_report),
        properties,
        show_stock_requirements: planning_table.show_stock_requirements,
        shifts,
        shift_x_positions,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(StockRequirementsGanttChart);
